import * as React from 'react';
import {
  List,
  Paper,
  makeStyles,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Tooltip,
  RadioGroup,
  Radio
} from '@material-ui/core';
import StyledCheckbox from '../../../common/checkbox';
import Tag from '../../../../models/tag';
import SecondaryButton from '../../../common/secondary-button';
import TertiaryButton from '../../../common/tertiary-button';
import { blue, neutral } from '../../../../theme/colors';
import { fontFamilies } from '../../../../theme/text';
import Client from '../../../../models/client';
import User, { AssignedClient, AssignedTag } from '../../../../models/user';
import { useFormikContext } from 'formik';
import SegmentationPermission from '../../../../models/enums/segmentationPermission';

const useStyles = makeStyles({
  paper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 200,
    width: '100%',
    overflow: 'auto',
    border: `1px solid ${neutral[300]}`,
    boxShadow: 'none',
    minHeight: '200px',
    maxHeight: '400px',
    margin: '0'
  },
  list: {
    flex: 1
  },
  selectAll: {
    color: blue[600],
    textTransform: 'uppercase',
    position: 'sticky',
    bottom: 0,
    boxSizing: 'border-box',
    width: '100%',
    margin: 0,
    padding: '8px 16px',
    backgroundColor: 'white'
  },
  loading: {
    display: 'flex',
    justifyContent: 'center'
  },
  modalActionFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '10px',
    '& button': {
      width: '70px'
    }
  },
  sectionHeadings: {
    fontFamily: fontFamilies.heading,
    color: neutral[600],
    fontWeight: 600,
    fontSize: 16,
    margin: 8
  },
  segmentationSection: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 200,
    width: '100%',
    border: `1px solid ${neutral[300]}`,
    boxShadow: 'none',
    marginBottom: 8,
    '& .MuiFormControlLabel-root': {
      margin: '4px 16px',
      '& .MuiFormControlLabel-label': {
        marginLeft: 16,
        fontFamily: fontFamilies.body,
        fontWeight: 400
      }
    }
  },
  listItem: {
    '& .MuiListItemIcon-root': {
      minWidth: '32px'
    }
  },
  checkbox: {
    '&.Mui-disabled': {
      color: 'transparent'
    }
  }
});

interface Props {
  /** all tags of the current client */
  clientTags: Tag[];
  closeModal: () => void;
  /** the relevant client for the user permissions currently being edited */
  client: Client | AssignedClient;
}

interface FSPermissions {
  clientId: number;
  segmentationPermission: SegmentationPermission;
}

/**
 * @function UserTagModalContent renders content of the user tag selection modal
 */
const UserTagModalContent = ({ clientTags, closeModal, client }: Props) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext<User>();
  const [checked, setChecked] = React.useState<AssignedTag[]>(
    values.assignedTags.filter((assingedTag) => assingedTag.clientId == client.clientId)
  );

  const enableFanSegmentation = React.useRef<boolean>(client.enableSegmentation);

  const clientFSPermission = React.useRef<FSPermissions>(
    values.assignedSegmentation.find((assignment) => assignment.clientId === client.clientId)
  );
  const [segmentAccess, setSegmentAccess] = React.useState<SegmentationPermission>(
    clientFSPermission.current.segmentationPermission
  );

  const handleToggle = (tag: Tag) => {
    const currentIndex = checked.map((checkedTag) => checkedTag.tagId).indexOf(tag.tagId);
    const newChecked = [...checked];
    if (currentIndex === -1) {
      newChecked.push(tag);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const saveHandler = () => {
    setFieldValue('assignedTags', [
      ...values.assignedTags.filter((assignedTag) => assignedTag.clientId !== client.clientId),
      ...checked
    ]);
    setFieldValue(
      'assignedSegmentation',
      values.assignedSegmentation.map((assignment) => {
        if (assignment.clientId === client.clientId) {
          return {
            ...assignment,
            segmentationPermission: segmentAccess
          };
        }
        return assignment;
      })
    );
    closeModal();
  };

  const selectAllHandler = () => {
    setChecked(clientTags);
  };

  return (
    <React.Fragment>
      <h2 className={classes.sectionHeadings}>Tags</h2>
      <Paper className={classes.paper}>
        <List dense role="list" className={classes.list}>
          {clientTags && clientTags.length > 0 ? (
            clientTags.map((tag) => {
              const labelId = `tag-checklist-label-${tag.tagId}`;
              return (
                <ListItem
                  key={tag.tagId}
                  role="listitem"
                  button
                  onClick={() => handleToggle(tag)}
                  className={classes.listItem}
                >
                  <ListItemIcon>
                    <StyledCheckbox
                      checked={checked.findIndex((checkedTag) => tag.tagId === checkedTag.tagId) !== -1}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-labelledby': labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={tag.name} />
                </ListItem>
              );
            })
          ) : (
            <ListItem>
              <ListItemText primary="No tags are currently available for this client." />
            </ListItem>
          )}
        </List>
        <p className={classes.selectAll}>
          {clientTags.length > 1 && (
            <TertiaryButton text="Select All" title="Select All" handleClick={selectAllHandler} />
          )}
        </p>
      </Paper>
      <h2 className={classes.sectionHeadings}>Fan Segmentation</h2>
      <Paper className={classes.segmentationSection}>
        <Tooltip
          title={!enableFanSegmentation.current ? 'Please enable the feature at the client level' : ''}
          arrow
        >
          <RadioGroup
            aria-label="segmentation-permission"
            name="segmentation-permission"
            value={segmentAccess}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSegmentAccess(Number(event.target.value));
            }}
          >
            <FormControlLabel
              disabled={!enableFanSegmentation.current}
              value={SegmentationPermission.None}
              control={<Radio />}
              label="None"
            />
            <FormControlLabel
              disabled={!enableFanSegmentation.current}
              value={SegmentationPermission.Basic}
              control={<Radio />}
              label="Basic User"
            />
            <FormControlLabel
              disabled={!enableFanSegmentation.current}
              value={SegmentationPermission.Push}
              control={<Radio />}
              label="Push User"
            />
            <FormControlLabel
              disabled={!enableFanSegmentation.current}
              value={SegmentationPermission.Power}
              control={<Radio />}
              label="Power User"
            />
          </RadioGroup>
        </Tooltip>
      </Paper>
      <div className={classes.modalActionFooter}>
        <SecondaryButton
          text="Save"
          title="Save"
          handleClick={saveHandler}
          deactive={!clientTags || clientTags.length == 0}
        />
      </div>
    </React.Fragment>
  );
};

export default UserTagModalContent;
